import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const StatisticsVisualization = ({ statistics, userData }) => {
  const [activeChart, setActiveChart] = useState('performance');

  const chartTypes = [
    { id: 'performance', label: 'Desempenho', icon: 'TrendingUp' },
    { id: 'venues', label: 'Locais', icon: 'MapPin' },
    { id: 'positions', label: 'Posições', icon: 'Users' },
    { id: 'activity', label: 'Atividade', icon: 'Calendar' }
  ];

  // Performance chart data
  const performanceData = statistics?.monthly?.labels?.map((label, index) => ({
    month: label,
    matches: statistics?.monthly?.matches?.[index] || 0,
    wins: statistics?.monthly?.wins?.[index] || 0,
    goals: statistics?.monthly?.goals?.[index] || 0
  }));

  // Venue chart data
  const venueData = statistics?.venues?.map((venue, index) => ({
    ...venue,
    winRate: venue?.matches > 0 ? Math.round((venue?.wins / venue?.matches) * 100) : 0,
    color: `hsl(${120 + index * 50}, 70%, 50%)`
  }));

  // Position pie chart data
  const positionData = Object?.entries(statistics?.positions || {})?.map(([position, matches], index) => ({
    name: position,
    value: matches,
    color: `hsl(${index * 72}, 70%, 60%)`
  }));

  // Weekly activity data
  const activityData = statistics?.weeklyActivity?.map(item => ({
    ...item,
    percentage: Math.round((item?.matches / Math.max(...statistics?.weeklyActivity?.map(d => d?.matches))) * 100)
  }));

  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-card-foreground">{`${label}`}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {`${entry?.dataKey}: ${entry?.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (activeChart) {
      case 'performance':
        return (
          <div className="space-y-4">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 12 }} 
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }} 
                    className="text-muted-foreground"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="matches" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    name="Partidas"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="wins" 
                    stroke="#82ca9d" 
                    strokeWidth={2}
                    name="Vitórias"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="goals" 
                    stroke="#ffc658" 
                    strokeWidth={2}
                    name="Gols"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="text-muted-foreground">Partidas</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span className="text-muted-foreground">Vitórias</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                <span className="text-muted-foreground">Gols</span>
              </div>
            </div>
          </div>
        );

      case 'venues':
        return (
          <div className="space-y-3">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={venueData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 10 }} 
                    className="text-muted-foreground"
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }} 
                    className="text-muted-foreground"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="matches" fill="#8884d8" name="Partidas" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="space-y-2">
              {venueData?.map((venue, index) => (
                <div key={venue?.name} className="flex items-center justify-between text-sm">
                  <span className="text-card-foreground font-medium truncate flex-1">
                    {venue?.name}
                  </span>
                  <span className="text-muted-foreground ml-2">
                    {venue?.winRate}% vitórias
                  </span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'positions':
        return (
          <div className="space-y-4">
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={positionData}
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    dataKey="value"
                  >
                    {positionData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {positionData?.map((position, index) => (
                <div key={position?.name} className="flex items-center space-x-2 text-sm">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS?.[index % COLORS?.length] }}
                  />
                  <span className="text-card-foreground font-medium">
                    {position?.name}
                  </span>
                  <span className="text-muted-foreground ml-auto">
                    {position?.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'activity':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              {activityData?.map((day) => (
                <div key={day?.day} className="flex items-center space-x-3">
                  <div className="w-8 text-xs font-medium text-muted-foreground">
                    {day?.day}
                  </div>
                  <div className="flex-1 bg-muted rounded-full h-4 relative">
                    <div 
                      className="bg-primary h-4 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                      style={{ width: `${(day?.matches / Math.max(...activityData?.map(d => d?.matches))) * 100}%` }}
                    >
                      <span className="text-xs font-medium text-primary-foreground">
                        {day?.matches}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="bg-muted/30 rounded-lg p-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Dia mais ativo:</span>
                  <div className="font-medium text-card-foreground">
                    {activityData?.reduce((max, day) => 
                      day?.matches > max?.matches ? day : max
                    )?.day || 'N/A'}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Total semanal:</span>
                  <div className="font-medium text-card-foreground">
                    {activityData?.reduce((sum, day) => sum + day?.matches, 0)} peladas
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="font-heading font-bold text-lg text-card-foreground mb-3">
          Estatísticas
        </h2>
        
        {/* Chart Type Selector */}
        <div className="grid grid-cols-2 gap-1 bg-muted rounded-lg p-1">
          {chartTypes?.map((chart) => (
            <button
              key={chart?.id}
              onClick={() => setActiveChart(chart?.id)}
              className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded transition-standard justify-center ${
                activeChart === chart?.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={chart?.icon} size={16} />
              <span className="hidden sm:inline">{chart?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Chart Content */}
      <div className="p-4">
        {renderChart()}
      </div>

      {/* Quick Stats Footer */}
      <div className="border-t border-border p-4 bg-muted/20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-card-foreground">
              {userData?.totalMatches || 0}
            </div>
            <div className="text-xs text-muted-foreground">Total de Partidas</div>
          </div>
          
          <div>
            <div className="text-lg font-bold text-success">
              {userData?.totalMatches > 0 ? Math.round((userData?.wins / userData?.totalMatches) * 100) : 0}%
            </div>
            <div className="text-xs text-muted-foreground">Taxa de Vitórias</div>
          </div>
          
          <div>
            <div className="text-lg font-bold text-primary">
              {userData?.goals || 0}
            </div>
            <div className="text-xs text-muted-foreground">Total de Gols</div>
          </div>
          
          <div>
            <div className="text-lg font-bold text-secondary">
              {userData?.totalMatches > 0 ? (userData?.goals / userData?.totalMatches)?.toFixed(1) : '0.0'}
            </div>
            <div className="text-xs text-muted-foreground">Média de Gols</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsVisualization;